import styled from 'styled-components';
import {useDispatch} from 'react-redux';

import {getLocalizationService} from '~/shared/services/localisationService';
import {media} from '~/shared/theme/media';
import Button from '~/shared/components/Button';
import {flipOnLTR, rotateOnLTR} from '~/shared/theme/flipOnLTR';
import {body14Normal, body20Bold} from '~/shared/theme/typography';
import useMyBenefits from '~/personalArea/components/Sections/MyBenefits/useMyBenefits';
import {SimpleSpinnerLoader} from '~/shared/components/Loaders';
import {setCurrentModal} from '~/shared/store/storeModules/currentModal/currentModalActions';
import {UserCoupon} from '~/shared/services/bffService/api/interfaces/responses';
import {CouponValueType} from '~/shared/store/models';

import {LoaderContainer, Title} from '../Shared';

import EmptyState from './EmptyState';
import BenefitIcon from './assets/benefitIcon.svg?react';
import InfoIcon from './assets/infoIcon.svg?react';

const Root = styled.div`
  padding: 24px 16px;
  ${media.minLargeMobile`
    padding: 32px;
  `}
  ${media.minLargeTablet`
    padding: 40px;
  `}
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
`;

const Benefit = styled.div`
  width: 100%;
  ${media.minLargeTablet`
    width: 389px;
  `}
  border-radius: 16px;
  box-shadow: 0 0 1px 0 rgba(0, 0, 0, 0.12), 0 1px 5px 0 rgba(0, 0, 0, 0.04), 0 0 4px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 144px;
`;

const TopSection = styled.div`
  display: flex;
  align-items: center;
  padding: 24px 20px;
  height: 96px;
  border-bottom: 1px dashed ${({theme}) => theme.colors.supportNeutral};
`;

const BottomSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  padding: 0 16px;
`;

const OrderNowButton = styled(Button)`
  width: 100%;
  height: 40px;
  font-weight: bold;
  background-color: ${({theme}) => theme.colors.actionSecondary};
  color: ${({theme}) => theme.colors.contentDefault};
  border-radius: 100px;

  &:hover {
    background-color: ${({theme}) => theme.colors.actionSecondaryHover};
  }
`;

const StyledBenefitIcon = styled(BenefitIcon)`
  width: 48px;
  height: 48px;
  ${rotateOnLTR}
`;

const BenefitTitle = styled.div`
  color: ${({theme}) => theme.colors.contentDefault};
  ${body20Bold}
  ${flipOnLTR`
    margin-right: 20px;
  `}
`;

const InfoLabel = styled.div`
  color: ${({theme}) => theme.colors.contentDefault};
  ${body14Normal}
  padding: 2px 12px;
  line-height: 1;
  ${flipOnLTR`
    border-left: 1px solid ${({theme}) => theme.colors.supportNeutral};
     &:last-child {
      border-left: none;
      padding-left: 0;
    }
    &:first-child {
      padding-right: 0;
    }
  `}
`;

const InfoButton = styled(OrderNowButton)`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${flipOnLTR`
    margin-right: auto;
  `}
`;
const shouldDisplayRemainingBalanceLabel = (benefit: UserCoupon) =>
  benefit.valueType === CouponValueType.AMOUNT &&
  !!benefit.couponsDiscountBalanceLeftPerUser &&
  benefit.couponsQuotaPerUser > 1;

const shouldDisplayQuotasLabel = (benefit: UserCoupon) =>
  benefit.couponsQuotaPerUser > 1;

const MyBenefits = () => {
  const {t} = getLocalizationService();
  const dispatch = useDispatch();

  const {coupons, isLoaded} = useMyBenefits();

  const onBenefitInfoClick = (benefitId: number) => {
    dispatch(setCurrentModal('discountInfoModal', {
      discountId: benefitId,
    }));
  };

  if (!isLoaded) {
    return (
      <LoaderContainer>
        <SimpleSpinnerLoader variant="pieBig" />
      </LoaderContainer>
    );
  }

  if (!coupons?.length) {
    return <EmptyState />;
  }

  return (
    <Root>
      <Title>{t('personal_area_page_your_benefits_tab')}</Title>
      <BenefitsList>
        {coupons.map(benefit => (
          <Benefit key={benefit.id} data-test-id={`benefitIndex_${benefit.id}`}>
            <TopSection>
              <StyledBenefitIcon viewBox="0 0 52 52"/>
              <BenefitTitle data-test-id="benefitTitle">{benefit.title}</BenefitTitle>
              <InfoButton data-test-id="benefitInfoButton" onClick={() => onBenefitInfoClick(benefit.id)}>
                <InfoIcon />
              </InfoButton>
            </TopSection>
            <BottomSection>
              {shouldDisplayRemainingBalanceLabel(benefit) && <InfoLabel data-test-id="benefitRemainingBalance">{t('remaining_balance_xxx', {remainingBalance: benefit.couponsDiscountBalanceLeftPerUser})}</InfoLabel>}
              {shouldDisplayQuotasLabel(benefit) && <InfoLabel data-test-id="benefitCouponsLeft">{t('left_xxx_of_xxx', {current: benefit.couponsLeftPerUser, total: benefit.couponsQuotaPerUser})}</InfoLabel>}
              <InfoLabel data-test-id="benefitExpirationDate">{t('voucher_valid_till', {date: benefit.expirationDate})}</InfoLabel>
            </BottomSection>
          </Benefit>
      ))}
      </BenefitsList>
    </Root>
  );
};

export default MyBenefits;
